import { useRef, useState, useEffect } from 'react';

const CLIENTS = [
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/Customer-1.jpg',
    company: 'Cleveland Bros Equipment',
    location: 'Harrisburg, PA',
    contact: 'Brian Clayton — Regional Highway Ops Manager',
    system: 'MAXJET MJ 5060 — Caterpillar Dealer',
    quote: 'The system has performed consistently since day one. Exactly what we needed for our Caterpillar equipment servicing operation. KC Quality delivered on every promise.',
  },
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/Customer-2.jpg',
    company: 'Consolidated Truck Parts',
    location: 'Monroe, LA',
    contact: 'Joe D Niswanger',
    system: 'MJ 5045 Gas Fired — Military Truck Engine Rebuilding',
    quote: 'Perfect for rebuilding military truck engines. The quality and durability of the MAXJET system exceeded our expectations at every level.',
  },
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/Customer-3.jpg',
    company: 'American Compressor Solutions',
    location: 'Gillette, WY',
    contact: 'Dirk Schimmel',
    system: '2 × MJ 5045 — Gas Compressor Rebuilding',
    quote: 'We run two units hard every day. The build quality is exceptional — these machines just keep going. Long-term reliability is exactly what we needed.',
  },
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/axelboy-logo.jpg',
    company: 'Axel Boy',
    location: 'St. Peters, MO',
    contact: 'Scott Carline',
    system: 'MJ 3045 — Axle Rebuilding',
    quote: 'Reliable, well-built, and exactly what we needed for our axle rebuilding operation. KC Quality Systems delivered a product that works hard and lasts.',
  },
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/airhydraulics-logo.jpg',
    company: 'Air Hydraulic Mechanical Services',
    location: 'Alberta, Canada',
    contact: 'Carey Nagle & Floyd Guest',
    system: 'MJ 3045 & MJ 5060 — Mining Equipment Repair',
    quote: 'Two units running in our mining equipment repair shop. Consistent performance and excellent support from the KC Quality team — we couldn\'t ask for more.',
  },
  {
    logo: 'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/anderson-global-logo.jpg',
    company: 'Anderson Global',
    location: 'Muskegon Heights, MI',
    contact: 'Brian Becklin',
    system: 'MJ 7260 — Large Steel Mold Cleaning',
    quote: 'For cleaning large steel molds, there\'s nothing better. The MJ 7260 handles the size and weight requirements perfectly. A true industrial workhorse.',
  },
];

export default function TestimonialsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="bg-[#0a0a0a] py-32 relative overflow-hidden" ref={ref}>
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-gold text-xs font-semibold tracking-[0.4em] uppercase font-sans">Trusted Clients</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-white mt-4 mb-4">
            Proven Results Across Industries
          </h2>
          <p className="text-white/40 font-sans text-lg max-w-xl mx-auto">
            From Caterpillar dealers to aerospace and mining operations — clients worldwide trust KC Quality Systems to deliver.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CLIENTS.map((client, idx) => (
            <div
              key={idx}
              className={`group relative bg-[#0d0d0d] rounded-xl p-6 border border-[#1a1a1a] hover:border-gold/20 transition-all duration-500 flex flex-col overflow-hidden ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              {/* Subtle hover glow */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/0 to-transparent group-hover:via-gold/30 transition-all duration-500" />

              {/* Stars */}
              <div className="flex items-center gap-0.5 mb-5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-sm" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-white/55 text-sm font-sans leading-relaxed italic flex-1 mb-6">
                &ldquo;{client.quote}&rdquo;
              </p>

              {/* Client info */}
              <div className="flex items-center gap-4 pt-5 border-t border-[#1a1a1a]">
                <div className="w-20 h-12 flex items-center justify-center flex-shrink-0 bg-white/5 rounded-lg p-2">
                  <img
                    src={client.logo}
                    alt={client.company}
                    className="max-h-8 max-w-full object-contain"
                  />
                </div>
                <div>
                  <p className="text-white font-bold text-sm font-sans">{client.company}</p>
                  <p className="text-white/30 text-xs font-sans mt-0.5">{client.location}</p>
                  <p className="text-gold text-xs font-sans mt-1">{client.system}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial section CTA */}
        <div className={`mt-14 transition-all duration-700 delay-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative rounded-xl overflow-hidden">
            <div className="absolute inset-0 opacity-15">
              <img
                src="https://public.readdy.ai/ai/img_res/a67004c6c8d58dd5cc4039a3528d20f4.jpg"
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 bg-gradient-to-r from-[#0d0d0d]/98 to-[#0d0d0d]/90 border border-[#1e1e1e] rounded-xl px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-gold text-base" />
                  ))}
                  <span className="text-white/40 text-sm font-sans ml-2">Trusted by industry leaders</span>
                </div>
                <h3 className="font-serif text-2xl text-white mb-2">Ready to Join Them?</h3>
                <p className="text-white/40 font-sans text-sm max-w-lg">
                  Get a system engineered for your operation and backed by 25+ years of proven industrial expertise.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 flex-shrink-0">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-gold text-black font-bold text-sm rounded-md hover:bg-gold-light transition-colors cursor-pointer font-sans whitespace-nowrap"
                >
                  Request a Consultation
                </a>
                <a
                  href="https://www.kc-qualitysystems.com/custom-washer-quote/"
                  className="px-8 py-4 border border-[#2a2a2a] text-white/60 font-semibold text-sm rounded-md hover:border-gold hover:text-gold transition-colors cursor-pointer font-sans whitespace-nowrap"
                >
                  Get a Custom Quote
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Partner logos */}
        <div className={`mt-12 flex flex-wrap justify-center items-center gap-10 transition-all duration-700 delay-700 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          {[
            'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/broadwing-energy.jpg',
            'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/brakepartsinc-logo.jpg',
            'https://www.kc-qualitysystems.com/wp-content/uploads/2019/02/andrewselectricmotorshop-logo.jpg',
          ].map((src, i) => (
            <img
              key={i}
              src={src}
              alt="KC Quality Systems partner client"
              className="h-9 object-contain brightness-0 invert opacity-25 hover:opacity-45 transition-opacity"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
